import React, { useEffect, useState, useRef, useId } from "react";
import "../../Scheduler/components/SchedulerFilters/SchedulerFilters.scss";
import { useAllStaffByDesignationIdAndOfficeId } from "repositories/notes-repository";
import { withTranslation } from "react-i18next";
import Select from "react-select";
import Loader from "components/Loader";
import styles from "../Dashboard.module.scss";
import { useStaffDesignation } from "repositories/scheduler-repository";
import Text from "components/Text";
import FilterIcon from "../../../../assets/images/clicked.svg";
import FilterDefaultIcon from "../../../../assets/images/default.svg";
import ExpandableSearchBar from "accountOwner/pages/AddEvent/SchedulingAssistant/components/ExpandableSearchBar";
import searchStyles from "accountOwner/pages/Scheduler/OfficeSchedulerTab/SubTabs/EventsShiftsTab/EventsShiftsTab.module.scss";

function StaffFilter({
  t,
  setOfficeFilter,
  selectedOffice,
  officeFilter,
  selectedRoles,
  ownerId,
  ownerData,
  setSelectedRoles,
  setUserList,
  resetSearchTrigger = 0, // Optional: increment this to reset the search bar
}) {
  // Unique per mounted instance so checkbox ids stay collision-free.
  const instanceId = useId();
  const [rolesData, setRolesData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectDropdown, setSelectDropdown] = useState(true);
  const [staffData, setStaffData] = useState([]);
  const [filteredStaffData, setFilteredStaffData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedOption, setSelectedOption] = useState({
    value: "",
    label: "All Roles",
  });

  const { isLoading: loadinStaffData, data: DashBoardRoleData } =
    useStaffDesignation();
  const { data, isLoading } = useAllStaffByDesignationIdAndOfficeId(
    selectedRoles,
    officeFilter,
    ownerId,
    { enabled: !!ownerId }
  );

  useEffect(() => {
    if (!loadinStaffData && DashBoardRoleData?.length) {
      let flatData = DashBoardRoleData?.map(
        (item) => item?.designations
      )?.flat();
      if (flatData?.length) {
        let data = flatData?.map((item) => {
          return {
            label: item?.name,
            value: item?.id,
          };
        });
        setRolesData([{ value: "", label: "All Roles" }, ...data]);
      }
    }
  }, [loadinStaffData, DashBoardRoleData]);

  const handleMemberType = (selected) => {
    setSelectedOption(selected);
    if (selected?.value) {
      setSelectedRoles([selected?.value]);
    } else {
      setSelectedRoles([]);
    }
  };

  useEffect(() => {
    if (!isLoading && data && data?.data) {
      let name = ownerData?.role?.id
        ? t("dashBoard.mySelf")
        : t("dashBoard.AccountOwner");
      const updatedStaff = [
        {
          id: ownerData?.id,
          userId: ownerData?.userId,
          user: { ...ownerData },
          designation: { name },
        },
        ...data.data,
      ];
      setStaffData(updatedStaff);
      setFilteredStaffData(updatedStaff);
      setUserList(updatedStaff);
    }
  }, [isLoading, data]);

  useEffect(() => {
    if (selectedOffice?.length === staffData?.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedOffice, staffData]);

  const handleChange = (e, item) => {
    if (e.target.checked) {
      setOfficeFilter([
        ...selectedOffice,
        { id: item?.id, userId: item?.userId },
      ]);
    } else {
      setOfficeFilter(selectedOffice.filter((office) => office.id !== item.id));
    }
    setSelectAll(false);
  };

  const handleAllChange = (checked = false) => {
    setSelectAll(checked);
    if (checked) {
      setOfficeFilter(
        staffData?.length
          ? staffData?.map((item) => {
              return {
                id: item?.id,
                userId: item?.userId,
              };
            })
          : []
      );
    } else {
      setOfficeFilter([]);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.toLowerCase();
    setSearchValue(value);

    if (value.trim() === "") {
      setFilteredStaffData(staffData);
    } else {
      setFilteredStaffData(
        staffData?.filter((item) =>
          `${item?.user?.firstName} ${item?.user?.lastName}`
            .toLowerCase()
            .includes(value)
        )
      );
    }
  };

  // Update filteredStaffData when staffData changes (e.g., when role filter changes or resets)
  useEffect(() => {
    if (staffData && staffData.length > 0) {
      // If there's a search value, filter by it, otherwise show all staff
      if (searchValue.trim() === "") {
        setFilteredStaffData(staffData);
      } else {
        setFilteredStaffData(
          staffData.filter((item) =>
            `${item?.user?.firstName} ${item?.user?.lastName}`
              .toLowerCase()
              .includes(searchValue)
          )
        );
      }
    } else {
      setFilteredStaffData([]);
    }
  }, [staffData, searchValue]);

  useEffect(() => {
    // Removed auto-selection of all staff when role is selected
    // Users should manually select staff members
    if (!selectedOffice?.length && !selectedOption?.value) {
      setOfficeFilter([]);
    }
  }, [selectedRoles, isLoading, staffData]);

  // Ensure staff checkboxes are cleared when selectedOffice becomes empty (from parent reset)
  useEffect(() => {
    if (!selectedOffice || selectedOffice.length === 0) {
      // The checkboxes are controlled by selectedOffice prop, so they should automatically uncheck
      // But we also need to clear the selectAll state
      setSelectAll(false);
    }
  }, [selectedOffice]);

  // Reset role filter dropdown when selectedRoles becomes empty (from parent reset)
  useEffect(() => {
    if (!selectedRoles || selectedRoles.length === 0) {
      const allRolesOption = { value: "", label: "All Roles" };
      setSelectedOption(allRolesOption);
    }
  }, [selectedRoles]);

  // Reset search when selectedOffice becomes empty (most reset functions do this)
  // Only reset if search has a value (to avoid resetting on initial load)
  const prevSelectedOffice = useRef(selectedOffice);
  useEffect(() => {
    const wasNotEmpty =
      prevSelectedOffice.current && prevSelectedOffice.current.length > 0;
    const isNowEmpty = !selectedOffice || selectedOffice.length === 0;

    if (wasNotEmpty && isNowEmpty && searchValue) {
      setSearchValue("");
      setFilteredStaffData(staffData);
    }

    prevSelectedOffice.current = selectedOffice;
  }, [selectedOffice, staffData, searchValue]);

  // Reset search and role filter when resetSearchTrigger changes
  // Use ref to track previous trigger value to avoid resetting when staffData changes
  const prevResetTrigger = useRef(0);
  useEffect(() => {
    if (resetSearchTrigger > prevResetTrigger.current) {
      // Reset search
      setSearchValue("");
      if (staffData.length > 0) {
        setFilteredStaffData(staffData);
      }

      // Reset role filter dropdown display to "All Roles"
      // Don't call setSelectedRoles here - let parent handle that via resetFilter
      const allRolesOption = { value: "", label: "All Roles" };
      setSelectedOption(allRolesOption);

      prevResetTrigger.current = resetSearchTrigger;
    }
  }, [resetSearchTrigger, staffData]);

  return (
    <>
      {isLoading && <Loader />}
      <div className={styles.flex_col_div}>
        <div className={styles.filter_search_icon_wrapper}>
          <div
            className={searchStyles["search-form"] + " " + styles["search-box"]}
          >
            <ExpandableSearchBar
              placeholder={t("accountOwner.searchByName")}
              searchValue={searchValue}
              setSearchValue={handleSearchChange}
              expanded
            />
          </div>
          <div
            onClick={() => {
              setSelectDropdown(!selectDropdown);
            }}
          >
            {selectDropdown ? (
              <img
                src={FilterIcon}
                alt="FilterIcon"
                className={styles.filter_icon}
              />
            ) : (
              <img
                src={FilterDefaultIcon}
                alt="FilterIcon"
                className={styles.filter_icon_default}
              />
            )}
          </div>
        </div>
        {selectDropdown && (
          <div
            className={
              styles.select_container_new +
              " " +
              "member-filter review-rating-filter all-option custom-scroll"
            }
          >
            <Select
              className={
                styles.react_select_box + " " + "react-select-container"
              }
              classNamePrefix="react-select"
              options={rolesData}
              value={selectedOption}
              onChange={handleMemberType}
              isSearchable={false}
            />
          </div>
        )}
        <ul className={["filter-list"]}>
          {filteredStaffData?.length ? (
            <ul>
              <li>
                <div className="ch-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      onChange={(e) => handleAllChange(e.target.checked)}
                      checked={selectAll}
                    />
                    <span>{t("taskManagement.allStaff")}</span>
                  </label>
                </div>
              </li>
              <hr className="mr-4" />
            </ul>
          ) : (
            <span>{t("dashBoard.notFoundStaff")}</span>
          )}

          {filteredStaffData &&
            filteredStaffData?.map((item, key) => {
              const checkboxId = `${instanceId}-staff-${item?.id ?? key}`;

              return (
                <div className={styles["checkbox_wrapper_filter"]} key={key}>
                  <div
                    className={
                      styles["check_box_width"] +
                      " " +
                      "ch-checkbox c-field all-event-checkbox m-0"
                    }
                  >
                    <label>
                      <input
                        type="checkbox"
                        id={checkboxId}
                        onChange={(e) => handleChange(e, item)}
                        checked={selectedOffice?.some(
                          (office) => office?.id === item?.id
                        )}
                      />
                      <span className={styles["checkbox_height"]}></span>
                    </label>
                  </div>
                  <div
                    className={
                      styles["name_office"] +
                      " " +
                      styles["clickable_name_office"]
                    }
                  >
                    <label className="m-0" htmlFor={checkboxId}>
                      <Text
                        size="14px"
                        weight="600"
                        color="#102C42"
                        className={styles.office_name}
                      >
                        {`${item?.user?.firstName} ${item?.user?.lastName}`}
                      </Text>
                      <Text size="12px" weight="400" color="#87928D">
                        {item?.designation?.name}
                      </Text>
                    </label>
                  </div>
                </div>
              );
            })}
        </ul>
      </div>
    </>
  );
}

export default withTranslation()(StaffFilter);
